import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { useMutation } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  registerTableMenu,
  unregisterTableMenu,
} from "src/charts/tableMenuRegistry";
import {
  downloadKpiCsv,
  downloadKpiPng,
  downloadKpiPdf,
  downloadNodePng,
  downloadNodePdf,
  type KpiExportData,
} from "src/helpers/widgetExport";
import { exportDashboardWidgetDataApi } from "src/api/dashboardApi";
import {
  downloadExportFile,
  type ReportExportResponse,
} from "src/helpers/Report";
import "./KpiExportMenu.scss";

const MENU_WIDTH = 190;

// has_download_options/download_options are backend-declared, sibling
// fields of the KPI's own data (same shape DownloadOptionsMenu.tsx uses for
// the other widget types) — not part of KpiExportData's own known fields,
// read via the trailing Record<string, unknown> it already carries.
type DownloadOptions = { csv?: boolean; case_data?: boolean };

type Props = {
  data: KpiExportData;
  title?: string;
  // Widget description from the dashboard detail API — included in the CSV.
  description?: string | null;
  // The widget root to snapshot for PNG/PDF. The menu prefers the enclosing
  // dashboard card (includes the title header) when one exists.
  targetRef: React.RefObject<HTMLElement | null>;
  // The card-header hamburger (WidgetRenderer → openTableMenu) is the only
  // trigger: this menu registers under the widget id used as container id.
  menuId?: string;
  // Needed only for the backend-driven "Download CSV Data"/"Download Case
  // Data" items below — every other item here is built client-side from
  // `data` already in memory.
  dashboardName?: string;
  // Deliberately required-but-nullable rather than optional: every call site
  // must decide explicitly. When it was optional, SparkleWidget silently
  // omitted it and its downloads shipped with no Applied Filters block.
  appliedFilters: Record<string, unknown> | undefined;
  // A fixed-size export view for image/PDF, rendered off-screen so the output
  // is crisp and full-size regardless of the on-screen tile size. Falls back
  // to snapshotting the live card when omitted.
  renderExport?: () => ReactNode;
  // Sparkline show/hide toggle — only rendered as a menu item when the
  // caller supplies both (SparkleWidget owns the hidden/shown state; export
  // snapshots always render a fresh instance with the chart shown, so this
  // toggle only ever affects the live on-screen view).
  isChartHidden?: boolean;
  onToggleChart?: () => void;
  // Backend-declared download capability. These arrive as SIBLINGS of the
  // response's `.data` (WidgetRenderer surfaces them from the raw response),
  // and sparkle/kpi have that envelope stripped before render — so they have
  // to be passed in rather than read off `data`, which is what every other
  // widget already does.
  hasDownloadOptions?: boolean;
  downloadOptions?: DownloadOptions;
  // The open tab's payload *key* (e.g. "medical_precert"), for sparkle
  // widgets whose data is split into segments. Sent to the export endpoint so
  // the server scopes the file to the tab the user is looking at, the way the
  // browser-built file used to. Undefined for a flat payload or for KPI.
  activeTabKey?: string;
};

export default function KpiExportMenu({
  data,
  title,
  description,
  targetRef,
  menuId,
  dashboardName,
  appliedFilters,
  renderExport,
  isChartHidden,
  onToggleChart,
  activeTabKey,
  hasDownloadOptions: hasDownloadOptionsProp,
  downloadOptions: downloadOptionsProp,
}: Props) {
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);
  const [busy, setBusy] = useState<"png" | "pdf" | "csv" | null>(
    null,
  );
  const menuRef = useRef<HTMLDivElement>(null);

  // Props first (the live dashboard, where the envelope is stripped upstream);
  // the payload is the fallback for callers that still pass the whole
  // envelope through as `data`.
  const hasDownloadOptions =
    hasDownloadOptionsProp ??
    !!(data as { has_download_options?: boolean }).has_download_options;
  const downloadOptions =
    downloadOptionsProp ??
    (data as { download_options?: DownloadOptions }).download_options;

  // "Download Details" is the only server-backed item here: the aggregate
  // Excel export is still built client-side from the payload already in
  // memory, so there is no second kind to distinguish.
  const { mutate: downloadBackendData, isPending: isBackendDownloadPending } =
    useMutation<ReportExportResponse, Error, void>({
      mutationFn: () => {
        if (!dashboardName || menuId === undefined) {
          throw new Error("Missing widget context for download.");
        }

        // Per-widget request mechanics go in their own object rather than
        // inside `filters` (which is the user's own filter selection).
        // `tab` is only meaningful for a segmented sparkle payload, and is
        // the segment KEY -- "medical_precert", not the "Med Precert" label.
        const widgetFilters: Record<string, unknown> = {
          download_level: "detail",
        };
        if (activeTabKey) widgetFilters.tab = activeTabKey;

        return exportDashboardWidgetDataApi(
          dashboardName,
          menuId,
          {
            filters: { ...(appliedFilters ?? {}), download_level: "detail" },
            widget_filters: widgetFilters,
          },
          "xlsx",
        );
      },
      onSuccess: async (response) => {
        const normalizedTitle =
          (title ?? "widget")
            .trim()
            .replace(/[\\/:*?"<>|]+/g, "_")
            .replace(/\s+/g, "_") || "widget";
        const suffix = "-case-data";
        try {
          await downloadExportFile(
            {
              ...response,
              filename: `${normalizedTitle}${suffix}.${response.format || "xlsx"}`,
            },
            // Note: the user's own filters, not the augmented `filters` sent to
            // the endpoint — top_n/download_level are request mechanics.
            { appliedFilters },
          );
        } catch {
          toast.error("Failed to process download file.");
        }
        close();
      },
      onError: (error) => {
        toast.error(error.message || "Failed to download widget data.");
        close();
      },
    });

  const openAt = useCallback((pos: { x: number; y: number }) => {
    setMenuPos({
      x: Math.min(
        Math.max(pos.x - MENU_WIDTH, 8),
        window.innerWidth - MENU_WIDTH - 8,
      ),
      y: Math.min(pos.y + 6, window.innerHeight - 220),
    });
  }, []);

  const close = useCallback(() => setMenuPos(null), []);

  // Let the dashboard card-header hamburger open this menu.
  useEffect(() => {
    if (!menuId) return;
    registerTableMenu(menuId, openAt);
    return () => unregisterTableMenu(menuId);
  }, [menuId, openAt]);

  // Outside click / Esc close.
  useEffect(() => {
    if (!menuPos) return;
    const onPointerDown = (e: PointerEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      close();
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("pointerdown", onPointerDown);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("pointerdown", onPointerDown);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [menuPos, close]);

  const snapshotTarget = (): HTMLElement | null => {
    const el = targetRef.current;
    if (!el) return null;
    return (el.closest(".card") as HTMLElement | null) ?? el;
  };

  const run = async (kind: "png" | "pdf" | "csv") => {
    try {
      if (kind === "csv") {
        setBusy(kind);
        await downloadKpiCsv(data, title, description, appliedFilters);
      } else {
        setBusy(kind);
        // Preferred: render the dedicated fixed-size export view off-screen so
        // the image is crisp and full-size no matter how small the tile is.
        const node = renderExport?.();
        if (node) {
          if (kind === "png") await downloadNodePng(node, data, title);
          else await downloadNodePdf(node, data, title);
        } else {
          const el = snapshotTarget();
          if (!el) return;
          if (kind === "png") await downloadKpiPng(el, data, title);
          else await downloadKpiPdf(el, data, title);
        }
      }
    } finally {
      setBusy(null);
      close();
    }
  };

  const item = (
    kind: "png" | "pdf" | "csv",
    icon: string,
    label: string,
    isDisabled = false,
  ) => (
    <button
      type="button"
      className="kpi-export__item"
      disabled={busy !== null || isDisabled}
      onClick={() => run(kind)}
    >
      <i
        className={`bi ${busy === kind ? "bi-hourglass-split" : icon}`}
        aria-hidden="true"
      />
      {label}
    </button>
  );

  return (
    <>
      {menuPos &&
        createPortal(
          <div
            ref={menuRef}
            className="kpi-export__menu"
            role="menu"
            style={{ left: menuPos.x, top: menuPos.y, width: MENU_WIDTH }}
            data-html2canvas-ignore="true"
          >
            {item("png", "bi-image", "Download PNG image")}
            {item("pdf", "bi-file-earmark-pdf", "Download PDF")}
            <div className="kpi-export__divider" />
            {item("csv", "bi-file-earmark-excel", "Download Excel Data")}
            {/* Server-generated. The payload no longer carries case_list, so
                this is gated on the backend's has_download_options flag — the
                same signal every other widget uses — and posts
                widget_filters.download_level "detail", plus the open tab's key
                for a segmented sparkle so the file matches what is on screen. */}
            <button
              type="button"
              className="kpi-export__item"
              disabled={
                // has_download_options alone, consistent with every other
                // chart type; download_options.case_data is not consulted.
                !hasDownloadOptions || isBackendDownloadPending
              }
              onClick={() => downloadBackendData()}
            >
              <i
                className={`bi ${
                  isBackendDownloadPending ? "bi-hourglass-split" : "bi-list-ol"
                }`}
                aria-hidden="true"
              />
              Download Case Data
            </button>

            {onToggleChart && (
              <>
                <div className="kpi-export__divider" />
                <button
                  type="button"
                  className="kpi-export__item"
                  onClick={() => {
                    onToggleChart();
                    close();
                  }}
                >
                  <i
                    className={`bi ${isChartHidden ? "bi-graph-up" : "bi-eye-slash"}`}
                    aria-hidden="true"
                  />
                  {isChartHidden ? "Show line chart" : "Hide line chart"}
                </button>
              </>
            )}
          </div>,
          document.body,
        )}
    </>
  );
}
